import { Box } from "@mui/material"
import type { FC } from "react"

import { useInvoiceDocumentDelete } from "../../hooks/useInvoiceDocumentDelete"
import { useInvoiceDocuments } from "../../hooks/useInvoiceDocuments"
import { useInvoiceFilesColumns } from "../../hooks/useInvoiceFilesColumns"

import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import { FilesTable } from "@/components/Tables/Aeros/components/FilesTable"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"

interface InvoiceFormDocumentsPaneProps {
  invoiceId: string
  disabled?: boolean
}

// Headerless variant of InvoiceDocuments (InvoiceRowDetails) — nests inside the
// InvoiceAttachmentsSection's "Attachments" FormControl, so it hides entirely rather than
// showing its own "Documents" title or an empty-state row when the invoice has no documents yet.
export const InvoiceFormDocumentsPane: FC<InvoiceFormDocumentsPaneProps> = ({ invoiceId, disabled }) => {
  const { documents, isFetching, refetch } = useInvoiceDocuments(invoiceId)
  const { hasPermissions } = usePermissionManager()
  const hasMaintenancePermission = !disabled && hasPermissions([PermissionActionType.ACCOUNTS_PAYABLE_MAINTENANCE])

  const { isConfirmationOpen, isDeleting, handleDeleteRequest, handleDeleteCancel, handleDeleteConfirm } =
    useInvoiceDocumentDelete(refetch)

  const columns = useInvoiceFilesColumns({ onDelete: hasMaintenancePermission ? handleDeleteRequest : undefined })

  if (isFetching || !documents.length) {
    return null
  }

  return (
    <Box className='flex flex-col gap-3' data-testid='invoice.form.documents-pane.container'>
      <FilesTable data={documents} columns={columns} isLoading={isFetching} />
      {isConfirmationOpen && (
        <ConfirmationDialog
          title='Remove Document'
          content='Are you sure you want to remove this document? This action cannot be undone.'
          closeLabel='Cancel'
          confirmLabel='Remove'
          confirmDisabled={isDeleting}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </Box>
  )
}
